import {
  GENERAL_GROUP_VISA_SERVICE,
  SPAIN_GROUP_VISA_SERVICE,
} from "@/components/program-manager/sessions/constants.js";
import {
  GROUP_VISA_ALLOWED_COUNTRIES,
  GV_ALLOWED_COUNTIES_NEW_JERSEY,
  GV_ALLOWED_STATES_ITALY,
  GV_ALLOWED_STATES_SPAIN,
  GV_ALLOWED_STATES_SPAIN_2,
} from "@/constants.js";
import store from "@/store/index.js";

/**
 * Creates an application id based on program session id, saves it into vuex too.
 * @param {String} programSessionId -> The program session id that comes from Program OR Learning Services
 * @returns {String} 32 character string
 */
export function V3_generateApplicationId(programSessionId = "") {
  const removedHyphens = programSessionId.replace(/-/g, "");
  const appId = (Date.now() + removedHyphens + Math.random().toString().substring(3)).substring(0, 32);
  store.commit("setCurrentApplicationId", appId);
  return appId;
}

/**
 * Iterates through a list of steps and determines:
 * Which ones should be shown - if they shouldn't be shown they're removed/spliced
 * Which ones are completed
 * Which ones can be interacted with
 * @param {Array} steps -> A list of step objects for SteppedProcess component, these can be editable
 * @param {Array} originalSteps -> A list of step objects that aren't editable, used for reference later
 * @param {Boolean} fetchSteps -> A flag that will do an api call to return the latest status of each step
 * @param {String} applicationId -> 32 character string
 * @param {Boolean} fetchProgramSession -> A flag to know if we should fetch the data of a program session
 * @returns {Array} A list of step objects
 */
export async function V3_setUpSteps(
  steps = [],
  originalSteps = [],
  fetchSteps = false,
  applicationId = "",
  fetchProgramSession = false,
) {
  if (fetchProgramSession && store.getters.getProgramSessionDataV3?.id) {
    await store.dispatch("setV3ProgramSession", store.getters.getProgramSessionDataV3.id);
  }
  // Get latest steps data if user has no data or flag is active
  if ((store.getters.getCurrentUser && store.getters.getStepsInV3[0].data === null) || fetchSteps) {
    await store.dispatch("getV3Steps", applicationId);
  }

  // Steps to remove
  // Account Creation - Remove if student is not applying for the first time
  const showAccountCreation = await V3_stepShouldBeShown("AccountCreation");
  if (!showAccountCreation) {
    const accountStep = find_stepIndex(steps, "new-account-creation");
    if (accountStep > -1) {
      steps.splice(accountStep, 1);
    }
  }

  // Group Visa - Remove if student doesn't need to complete form otherwise add it back if it was removed previously
  const showGroupVisa = await V3_stepShouldBeShown("GroupVisa");
  const visaStep = find_stepIndex(steps, "application-group-visa");
  if (showGroupVisa && visaStep === -1) {
    // If step doesn't exists we'll add it before Checkout
    const leanerInfoStep = find_stepIndex(steps, "application-learner-info");
    const newVisaStep = find_stepIndex(originalSteps, "application-group-visa");
    if (leanerInfoStep > -1 && newVisaStep > -1) {
      const nextStepAfterLearnerInfo = leanerInfoStep + 1;
      steps.splice(nextStepAfterLearnerInfo, 0, originalSteps[newVisaStep]);
    }
  } else if (!showGroupVisa) {
    // Remove step (If it exists)
    if (visaStep > -1) steps.splice(visaStep, 1);
  }

  // Next Steps - Remove if checkout isn't complete, add it otherwise
  const showNextSteps = await V3_stepShouldBeShown("NextSteps");
  const nextStepsStep = find_stepIndex(steps, "application-next-steps");
  if (showNextSteps && nextStepsStep === -1) {
    const newNextStepsStep = find_stepIndex(originalSteps, "application-next-steps");
    if (newNextStepsStep > -1) {
      // Add step at the end
      steps.push(originalSteps[newNextStepsStep]);
    }
  } else if (!showNextSteps) {
    if (nextStepsStep > -1) steps.splice(nextStepsStep, 1);
  }

  // Steps completed - Only check completion for logged in users
  if (store.getters.getCurrentUser) {
    // Account Creation
    const accountStep = find_stepIndex(steps, "new-account-creation");
    if (accountStep > -1) {
      const completed = store.getters.getStepsInV3[0]["completed"];
      steps[accountStep]["isComplete"] = () => completed;
    }

    // About You
    const aboutYouStep = find_stepIndex(steps, "application-about-you");
    if (aboutYouStep > -1) {
      const completed = store.getters.getStepsInV3[1]["completed"];
      steps[aboutYouStep]["isComplete"] = () => completed;
    }

    // Learner Information
    const learnerInfoStep = find_stepIndex(steps, "application-learner-info");
    if (learnerInfoStep > -1) {
      const completed = store.getters.getStepsInV3[2]["completed"];
      steps[learnerInfoStep]["isComplete"] = () => completed;
    }

    // Review
    const reviewStep = find_stepIndex(steps, "application-review");
    if (reviewStep > -1) {
      const completed = store.getters.getStepsInV3[3]["completed"];
      steps[reviewStep]["isComplete"] = () => completed;
    }

    // Group Visa
    const visaStep = find_stepIndex(steps, "application-group-visa");
    if (visaStep > -1) {
      const completed = store.getters.getStepsInV3[4]["completed"];
      steps[visaStep]["isComplete"] = () => completed;
    }

    // Checkout
    const checkoutStep = find_stepIndex(steps, "application-checkout");
    if (checkoutStep > -1) {
      const completed = store.getters.getStepsInV3[5]["completed"];
      steps[checkoutStep]["isComplete"] = () => completed;
    }

    // Next Steps
    const nextStepsStep = find_stepIndex(steps, "application-next-steps");
    if (nextStepsStep > -1) {
      const completed = store.getters.getStepsInV3[6]["completed"];
      steps[nextStepsStep]["isComplete"] = () => completed;
    }
  }

  // Steps selectable - They can click to return to them
  if (store.getters.getCurrentUser) {
    const aboutYouStep = find_stepIndex(steps, "application-about-you");
    if (aboutYouStep > -1) {
      const canReturn = await V3_stepCanBeReturned("AboutYou");
      steps[aboutYouStep]["isSelectable"] = () => canReturn;
    }

    const learnerInfoStep = find_stepIndex(steps, "application-learner-info");
    if (learnerInfoStep > -1) {
      const canReturn = await V3_stepCanBeReturned("LearnerInfo");
      steps[learnerInfoStep]["isSelectable"] = () => canReturn;
    }

    const reviewStep = find_stepIndex(steps, "application-review");
    if (reviewStep > -1) {
      const canReturn = await V3_stepCanBeReturned("Review");
      steps[reviewStep]["isSelectable"] = () => canReturn;
    }

    const visaStep = find_stepIndex(steps, "application-group-visa");
    if (visaStep > -1) {
      const canReturn = await V3_stepCanBeReturned("GroupVisa");
      steps[visaStep]["isSelectable"] = () => canReturn;
    }

    const checkoutStep = find_stepIndex(steps, "application-checkout");
    if (checkoutStep > -1) {
      const canReturn = await V3_stepCanBeReturned("Checkout");
      steps[checkoutStep]["isSelectable"] = () => canReturn;
    }

    const nextStepsStep = find_stepIndex(steps, "application-next-steps");
    if (nextStepsStep > -1) {
      const canReturn = await V3_stepCanBeReturned("NextSteps");
      steps[nextStepsStep]["isSelectable"] = () => canReturn;
    }
  }
  return steps;
}

/**
 * Navigation guard: Checks if user should be logged in
 * @returns {Boolean | Object} Boolean true will continue navigation, the object will redirect to the route set.
 */
export function V3_rejectNavigation() {
  if (!store.getters.getCurrentUser) {
    return { name: "landing-page" };
  }
  return true;
}

/**
 * Navigation guard: Only unauthenticated users can see thi
 * @returns {Boolean | Object} Boolean true will continue navigation, the object will redirect to the route set.
 */
export function V3_rejectNavigation_no_auth() {
  if (store.getters.getCurrentUser) {
    // Flag not active, go back
    return { name: "landing-page" };
  }
  return true;
}

export function V3_rejectNavigation_no_program_session() {
  if (!store.getters.isLoggedIn) {
    if (
      !store.state?.configurator?.currentOrder?.program_page_id ||
      !store.state?.configurator?.currentOrder?.session_id
    ) {
      return { name: "home" };
    }

    return true;
  }

  return true;
}

/**
 * Check if step should be shown
 * @param {String} step_name The name of the step we're trying to check, each name has a different way to check if it will be shown
 * @returns {Boolean}
 */
export async function V3_stepShouldBeShown(step_name = "") {
  let stepShouldBeShown = false;
  switch (step_name) {
    case "AccountCreation": {
      stepShouldBeShown = store.getters.getNewAccountAndApplication === true;
      break;
    }
    case "GroupVisa": {
      const GROUP_VISA_IDS = [GENERAL_GROUP_VISA_SERVICE.id, SPAIN_GROUP_VISA_SERVICE.id];
      const session_availabe_products = store.getters.getProgramSessionDataV3?.available_products ?? [];

      const session_uses_group_visa =
        Array.isArray(session_availabe_products) &&
        session_availabe_products.some((available_product) => GROUP_VISA_IDS.includes(available_product?.id));

      // If the session doesn't require visa, all the following logic is irrelevant
      if (!session_uses_group_visa) break;

      const user_country = store.getters.getProfileData?.current_address_country_id ?? "";
      const user_country_permanent = store.getters.getProfileData?.permanent_address_country_id ?? "";
      const state_current_address = store.getters.getProfileData?.current_address_state ?? "";
      const state_permanent_address = store.getters.getProfileData?.permanent_address_state ?? "";
      // TODO: What happens if session_travel_details > 1?
      const session_country =
        store.getters.getProgramSessionDataV3?.session_travel_details?.[0]?.arrival_city?.country?.id ?? "";

      const current_address_county = store.getters.getProfileData?.current_address_administrative_area_level_2 ?? "";
      const permanent_address_county =
        store.getters.getProfileData?.permanent_address_administrative_area_level_2 ?? "";

      const newJerseyCounties = new Set(GV_ALLOWED_COUNTIES_NEW_JERSEY);
      const currentAddressIsNewJerseyAndHasCountyAndSessionCountryIsItaly =
        state_current_address === "New Jersey" &&
        newJerseyCounties.has(current_address_county) &&
        session_country === "IT";
      const permanentAddressIsNewJerseyAndHasCountyAndSessionCountryIsItaly =
        state_permanent_address === "New Jersey" &&
        newJerseyCounties.has(permanent_address_county) &&
        session_country === "IT";

      // Program Session requires Visa and matches list of allowed countries
      if (GROUP_VISA_ALLOWED_COUNTRIES.includes(session_country)) {
        // Program Session country is Italy and state is either...
        if (
          session_country === "IT" &&
          (GV_ALLOWED_STATES_ITALY.includes(state_current_address) ||
            GV_ALLOWED_STATES_ITALY.includes(state_permanent_address))
        ) {
          stepShouldBeShown = true;
        }

        const states = [state_current_address, state_permanent_address].filter(Boolean);
        const allowedSpainStates = new Set([...GV_ALLOWED_STATES_SPAIN, ...GV_ALLOWED_STATES_SPAIN_2]);

        // Program Session country is Spain and state is either...
        const isSpainEligible = session_country === "ES" && states.some((s) => allowedSpainStates.has(s));
        if (isSpainEligible) {
          stepShouldBeShown = true;
        }
      }

      if ([state_current_address, state_permanent_address].includes("New York") && session_country === "IT") {
        stepShouldBeShown = true;
      }

      if ([user_country, user_country_permanent].includes("BM") && session_country === "IT") {
        stepShouldBeShown = true;
      }

      if (
        permanentAddressIsNewJerseyAndHasCountyAndSessionCountryIsItaly ||
        currentAddressIsNewJerseyAndHasCountyAndSessionCountryIsItaly
      ) {
        stepShouldBeShown = true;
      }
      break;
    }
    case "NextSteps": {
      stepShouldBeShown = store.getters.getStepsInV3[5].completed;
      break;
    }
  }
  return stepShouldBeShown;
}

/**
 * Check if step can be clicked
 * clicked = return to step
 * @param {String} step_name The name of the step we're trying to check, each name has a different way to check if it can clicked
 * @returns {Boolean}
 */
export async function V3_stepCanBeReturned(step_name = "") {
  let stepCanReturn = false;
  const accountCreationCompleted = store.getters.getAccountCreation?.completed ?? false;
  const aboutYouCompleted = store.getters.getAboutYouStep?.completed ?? false;
  const learnerInfoCompleted = store.getters.getLearnerInfoStep?.completed ?? false;
  const reviewCompleted = store.getters.getReviewStep?.completed ?? false;
  const groupVisaCompleted = store.getters.getGroupVisaStep?.completed ?? false;
  const checkoutCompleted = store.getters.getCheckoutStep?.completed ?? false;
  const nextStepsCompleted = store.getters.getNextSteps?.completed ?? false;

  switch (step_name) {
    case "AccountCreation": {
      // NEVEER
      break;
    }
    case "AboutYou": {
      const aboutIsNextStep = accountCreationCompleted && !aboutYouCompleted;
      stepCanReturn = aboutYouCompleted || aboutIsNextStep;
      break;
    }
    case "LearnerInfo": {
      const learnerInfoIsNextStep = aboutYouCompleted && !learnerInfoCompleted;
      stepCanReturn = learnerInfoCompleted || learnerInfoIsNextStep;
      break;
    }
    case "Review": {
      const showGroupVisa = await V3_stepShouldBeShown("GroupVisa");
      const reviewIsNextStep = showGroupVisa
        ? groupVisaCompleted && !reviewCompleted
        : learnerInfoCompleted && !reviewCompleted && !checkoutCompleted;

      stepCanReturn = reviewCompleted || reviewIsNextStep;
      break;
    }
    case "GroupVisa": {
      const showGroupVisa = await V3_stepShouldBeShown("GroupVisa");
      const groupVisaIsNextStep = showGroupVisa && learnerInfoCompleted;

      stepCanReturn = groupVisaIsNextStep && !checkoutCompleted && (groupVisaCompleted || !groupVisaCompleted);
      break;
    }
    case "Checkout": {
      const showGroupVisa = await V3_stepShouldBeShown("GroupVisa");
      const checkoutIsNextStep = showGroupVisa
        ? groupVisaCompleted && reviewCompleted && !checkoutCompleted
        : reviewCompleted && !checkoutCompleted;

      stepCanReturn = checkoutCompleted || checkoutIsNextStep;
      break;
    }
    case "NextSteps": {
      const isNextStep = checkoutCompleted;
      stepCanReturn = isNextStep || nextStepsCompleted;
      break;
    }
  }
  return stepCanReturn;
}

/**
 * Find index of an item on a list based on the slug property
 * @param {Array} steps The list to search in [haystack]
 * @param {String} stepSlug The name of the step we're trying to find [needle]
 * @returns {number}
 */
function find_stepIndex(steps = [], stepSlug = "") {
  return steps.findIndex((step) => step.slug === stepSlug);
}

export function redirectIfFeatureFlagNotEnabled(featureFlag, routeName = "landing-page") {
  const isFeatureEnabled = store.state.featureFlags[featureFlag];
  if (!isFeatureEnabled) {
    return { name: routeName };
  }
  return true;
}
